import { useEffect } from 'react'
import { useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'

import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import Card from '@10xLabs/react-web/components/Card'
import { FiltersContainer } from 'components/v2/Content'
import { DATE_RANGE, getDateRange, sanitizeDateRange } from 'features/App/components/Filters/DateRangeFilter'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { ChangesHeader } from 'features/Changes/components/Header'
import { ChangesTabs } from 'features/Changes/components/Tabs'
import { ChangesContent } from 'features/Changes/components/Content'
import { ChangesFilters } from 'features/Changes/components/Filters'
import { ChangesList } from 'features/Changes/components/List'
import { ChangesPagination } from 'features/Changes/components/Pagination'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { getDateRangeQuery } from 'utils/date'
import { filterEmptyValue } from 'utils/object'
import { getQueryString, getQueries } from 'utils/route'
import { canRead } from 'features/Users/utils'
import { useCompanyAutocomplete } from 'features/Companies/hooks'
import { getTimezone } from 'utils/date'
import { useLogin } from 'features/Login/hooks'
import { useUserSuggestionsByIdsQuery } from 'features/Users/hooks'
import { GET_EXECUTED_CHANGES } from 'features/Changes/queries'
import { ChangeCategory, ChangesQueryInput } from 'gql/generated/graphql'

const LIMIT = 30
const DATE_RANGE_DAYS_LIMIT = 90

const messages = defineMessages({
  helmet: {
    id: 'changes.helmet',
    defaultMessage: 'Changes | Nexbus Admin'
  }
})

const ExecutedChangesView = (): TSFixMe => {
  const navigate = useNavigate()
  const location = useLocation()
  const intl = useIntl()

  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  let content
  const {
    page = '1',
    id = '',
    types = '',
    serviceCode = '',
    dateRangeFrom,
    dateRangeTo,
    companyId,
    userId
  } = getQueries(location.search)
  let { dateRangeValue } = getQueries(location.search)

  const pageNb = parseInt(page)
  const typesList = types ? types.split(',') : []
  if (!(dateRangeFrom || dateRangeTo || dateRangeValue)) {
    dateRangeValue = DATE_RANGE.TODAY
  }
  const sanitizedDateRange = sanitizeDateRange({
    from: dateRangeFrom,
    to: dateRangeTo,
    rangeValue: dateRangeValue
  })

  const companyAutocomplete = useCompanyAutocomplete(companyId, {
    skip: !companyId
  })
  const usersAutocompleteResponse = useUserSuggestionsByIdsQuery({
    ids: !isEmpty(userId) ? [userId] : [],
    skip: !userId
  })
  const userAutocomplete = !isEmpty(usersAutocompleteResponse?.users) ? usersAutocompleteResponse.users.at(0) : null

  const filters = {
    id,
    dateRange: sanitizedDateRange,
    types: typesList,
    serviceCode,
    company: { id: companyAutocomplete?.id, name: companyAutocomplete?.name },
    user: {
      id: userAutocomplete?.id,
      username: userAutocomplete?.username,
      firstName: userAutocomplete?.firstName,
      lastName: userAutocomplete?.lastName
    }
  }

  const { loading, error, data, refetch, networkStatus } = useQuery(GET_EXECUTED_CHANGES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: filterEmptyValue({
      pagination: {
        page: pageNb - 1,
        size: LIMIT
      },
      input: filterEmptyValue({
        id,
        timezone: getTimezone(),
        category: ChangeCategory.Executed,
        dateRange: getDateRange(sanitizedDateRange),
        types: typesList,
        serviceCode,
        companyID: companyId,
        userID: userId
      }) as ChangesQueryInput
    })
  })

  useEffect(() => {
    refetch()
  }, [])

  const setPage = (pageNb: number): void => {
    const dateRangeQuery = getDateRangeQuery(filters.dateRange)
    const query = {
      page: pageNb,
      id: filters.id,
      dateRangeFrom: dateRangeQuery.from,
      dateRangeTo: dateRangeQuery.to,
      dateRangeValue: dateRangeQuery.rangeValue,
      serviceCode: filters.serviceCode,
      types: filters.types.join(','),
      companyID: filters.company.id,
      userID: filters.user.id
    }

    navigate(`/changes?${getQueryString(query)}`)
  }

  const hasAppliedFilters = !isEmpty(filterEmptyValue(filters))

  if (!canRead(FEATURES.CHANGES, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.CHANGES} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="changes.executed.error.description"
            defaultMessage="There was an error while loading your executed changes"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="changes.executed.error.action" defaultMessage="Reload your executed changes" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.changes.items) && pageNb === 1 && !hasAppliedFilters) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="changes.executed.empty.description"
            defaultMessage="You don&#39;t have any executed changes yet"
          />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.changes.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="changes.executed.empty.filters.description"
            defaultMessage="There was no executed changes found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/changes" kind="primary">
            <FormattedMessage id="changes.executed.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <ChangesList changes={data?.changes.items} showBookings />
        <ChangesPagination
          page={pageNb}
          limit={LIMIT}
          totalItems={data?.changes.total}
          currentItemsCount={data?.changes.items.length}
          setPage={setPage}
          filters={filters}
        />
      </>
    )
  }

  return (
    <>
      <ChangesHeader />
      <ChangesTabs />
      <ChangesContent>
        <FiltersContainer>
          {/* @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message*/}
          <ChangesFilters filters={filters} dateRangeDaysLimit={DATE_RANGE_DAYS_LIMIT} />
        </FiltersContainer>
        <Card.Container>
          <Card.Content noHeader noFooter>
            {content}
          </Card.Content>
        </Card.Container>
      </ChangesContent>
    </>
  )
}

export default withLastUrl(ExecutedChangesView)
